/**
 * Notifications API + TanStack Query hooks. Server-backed: the backend
 * writes a row per person when something happens (homework set, handed in,
 * marked, a wrap-up published, a booking, an application, a reminder), and
 * read state and preferences live on the server, so they follow the person
 * across devices.
 *
 *   GET  /notifications/                 paginated feed, ?unread=true
 *   GET  /notifications/unread-count/    polled for the bell badge
 *   POST /notifications/read/            {ids} or {} for all
 *   GET  /notifications/preferences/     kinds for this role, with channels
 *   PUT  /notifications/preferences/     {channels: {kind: {in_app, email}}}
 */

import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
  type InfiniteData,
  type UseInfiniteQueryResult,
  type UseMutationResult,
  type UseQueryResult,
} from "@tanstack/react-query";

import { api } from "./client";
import { toQueryString, type Paginated } from "./types";

/* ─────────────────────────────────────────── Shapes */

export type NotificationKind =
  | "lesson_reminder"
  | "wrapup_published"
  | "report_published"
  | "ai_concern"
  | "wrapup_due"
  | "homework_set"
  | "homework_due"
  | "homework_submitted"
  | "homework_graded"
  | "booking_new"
  | "application_new"
  | "message";

export interface AppNotification {
  id: number;
  kind: NotificationKind;
  /** Bold lead: a person or a piece of work. */
  title: string;
  /** The rest of the sentence. */
  body: string;
  /** In-app path to open, may be empty. */
  url: string;
  created_at: string;
  read_at: string | null;
  unread: boolean;
}

export interface NotificationChannel {
  kind: NotificationKind;
  label: string;
  description: string;
  in_app: boolean;
  email: boolean;
  /** Money, safety and account emails: always sent, the switch is locked. */
  always?: boolean;
}

/* ─────────────────────────────────────────── Endpoints */

const PAGE_SIZE = 15;

function listNotifications(page: number, unreadOnly: boolean): Promise<Paginated<AppNotification>> {
  return api
    .get<Paginated<AppNotification>>(
      `/notifications/${toQueryString({ page, page_size: PAGE_SIZE, unread: unreadOnly ? "true" : undefined })}`,
    )
    .then((r) => r.data);
}

/* ─────────────────────────────────────────── Queries */

const KEY = ["notifications"] as const;

/** Unread count for the bell. Polls every minute and on window focus. */
export function useUnreadCount(enabled = true): UseQueryResult<number> {
  return useQuery({
    queryKey: [...KEY, "unread-count"],
    queryFn: () => api.get<{ count: number }>("/notifications/unread-count/").then((r) => r.data.count),
    enabled,
    refetchInterval: 60_000,
    refetchOnWindowFocus: true,
    staleTime: 15_000,
  });
}

export function useNotificationFeed(
  unreadOnly: boolean,
  enabled = true,
): UseInfiniteQueryResult<InfiniteData<Paginated<AppNotification>>> {
  return useInfiniteQuery({
    queryKey: [...KEY, "feed", { unreadOnly }],
    queryFn: ({ pageParam }) => listNotifications(pageParam, unreadOnly),
    initialPageParam: 1,
    getNextPageParam: (last) => (last.page < last.pages ? last.page + 1 : undefined),
    enabled,
    staleTime: 15_000,
  });
}

/* ─────────────────────────────────────────── Mutations */

/** Mark some (ids) or all (no ids) as read. Updates the cache first so the
 *  dot and the badge clear instantly, then reconciles with the server. */
export function useMarkNotificationsRead(): UseMutationResult<unknown, unknown, number[] | undefined> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (ids?: number[]) => api.post("/notifications/read/", ids ? { ids } : {}),
    onMutate: async (ids) => {
      await qc.cancelQueries({ queryKey: KEY });
      const now = new Date().toISOString();
      const hit = (n: AppNotification) => n.unread && (!ids || ids.includes(n.id));
      let cleared = 0;
      qc.setQueriesData<InfiniteData<Paginated<AppNotification>>>({ queryKey: [...KEY, "feed"] }, (data) => {
        if (!data) return data;
        return {
          ...data,
          pages: data.pages.map((p) => ({
            ...p,
            results: p.results.map((n) => {
              if (!hit(n)) return n;
              cleared += 1;
              return { ...n, unread: false, read_at: now };
            }),
          })),
        };
      });
      qc.setQueryData<number>([...KEY, "unread-count"], (c) => (c === undefined ? c : ids ? Math.max(0, c - cleared) : 0));
    },
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: KEY });
    },
  });
}

/* ─────────────────────────────────────────── Preferences */

export function useNotificationPreferences(enabled = true): UseQueryResult<NotificationChannel[]> {
  return useQuery({
    queryKey: [...KEY, "preferences"],
    queryFn: () => api.get<{ kinds: NotificationChannel[] }>("/notifications/preferences/").then((r) => r.data.kinds),
    enabled,
  });
}

export function useSaveNotificationPreference(): UseMutationResult<
  NotificationChannel[],
  unknown,
  { kind: NotificationKind; in_app: boolean; email: boolean },
  { previous: NotificationChannel[] | undefined }
> {
  const qc = useQueryClient();
  const key = [...KEY, "preferences"];
  return useMutation({
    mutationFn: ({ kind, in_app, email }) =>
      api
        .put<{ kinds: NotificationChannel[] }>("/notifications/preferences/", { channels: { [kind]: { in_app, email } } })
        .then((r) => r.data.kinds),
    onMutate: async (next) => {
      await qc.cancelQueries({ queryKey: key });
      const previous = qc.getQueryData<NotificationChannel[]>(key);
      qc.setQueryData<NotificationChannel[]>(key, (list) =>
        list?.map((k) => (k.kind === next.kind ? { ...k, in_app: next.in_app, email: next.email } : k)),
      );
      return { previous };
    },
    onError: (_err, _next, ctx) => {
      if (ctx?.previous) qc.setQueryData(key, ctx.previous);
    },
    onSuccess: (kinds) => {
      qc.setQueryData(key, kinds);
    },
  });
}
